/**
 * Nombres legibles de `settings/featureFlags` (Gabor 27/10: «salen comandos, no nombres»).
 * La clave sigue siendo la técnica; aquí solo el texto que ve el admin y qué pasa al apagarlo.
 */
export interface FlagInfo {
  name: string;
  hint: string;
  group: 'Inteligencia artificial' | 'Funciones de la plataforma' | 'Seguridad y moderación';
}

/**
 * Interruptores que de verdad hacen algo hoy (27/10). El resto está en settings/featureFlags desde el
 * plan, pero el código aún no los mira: el panel los enseña como «no conectado» en vez de engañar.
 */
export const WIRED_FLAGS = new Set([
  'ai.listing_from_photos',
  'ai.listing_translation',
  'ai.search_query_translation',
  'ai.chat_translation',
  'ai.chat_scam_detector',
  'ai.photo_background',
  'ai.dispute_assistant',
  'ai.support_bot',
  'ai.social_copy',
  'forum',
  'listing_human_review',
  'rewards',
  'chat_spam_guard',
  // 2.1 §0 (4/11): conectados en servidor y web; al apagar se respeta lo ya pagado.
  'pro_subscriptions',
  'featured_packs',
  'bumps',
  'handover_mode',
  'social_publisher',
  'country_signup_guard',
  'nuvvu_club',
  'promo_no_commission',
  'promo_cashback',
  // 2.1 §3 (4/11): API de vendedores y feeds CSV/XML («Conectar mi tienda»).
  'seller_api',
  // 4/11: anuncios (web + job diario de banners PRO Absoluto).
  'video_ads',
  'pro_auto_banners',
  // 2.1 bloque C (IA).
  'ai.semantic_search',
  'ai.similar_items',
  'ai.price_suggestion',
  'ai.seller_suggested_replies',
  'ai.pre_moderation',
  'ai.duplicate_accounts',
]);

/** 2.1 §6: interruptores que admiten el estado «Solo admins» (el servidor lo comprueba con el usuario). */
export const ADMIN_ONLY_FLAGS = new Set([
  'promo_no_commission',
  'promo_cashback',
  'rewards',
  'pro_subscriptions',
  'featured_packs',
  'bumps',
  'handover_mode',
  'nuvvu_club',
  'seller_api',
  'video_ads',
  'pro_auto_banners',
  'ai.semantic_search',
  'ai.similar_items',
  'ai.price_suggestion',
  'ai.seller_suggested_replies',
  'ai.pre_moderation',
  'ai.duplicate_accounts',
]);

export const FLAG_INFO: Record<string, FlagInfo> = {
  seller_api: {
    name: 'Conectar mi tienda',
    hint: 'API para vendedores (claves en Mi cuenta) e importación periódica de feeds CSV/XML, solo PRO con perfil profesional verificado. Apagado: la API responde «función desactivada» y no se importa ningún feed; los anuncios ya publicados se quedan como están (no se borra ni se pausa nada).',
    group: 'Funciones de la plataforma',
  },
  'ai.listing_from_photos': {
    name: 'Crear anuncio a partir de fotos',
    hint: 'La IA rellena título, descripción, categoría y precio con las fotos.',
    group: 'Inteligencia artificial',
  },
  'ai.listing_translation': {
    name: 'Traducir anuncios',
    hint: 'Títulos a los 8 idiomas principales y botón «Traducir» en la descripción.',
    group: 'Inteligencia artificial',
  },
  'ai.search_query_translation': {
    name: 'Buscar en varios idiomas',
    hint: 'Lo que se escribe en el buscador también se busca en húngaro, español e inglés.',
    group: 'Inteligencia artificial',
  },
  'ai.chat_translation': {
    name: 'Traducir el chat',
    hint: 'Cada uno lee los mensajes en su idioma.',
    group: 'Inteligencia artificial',
  },
  'ai.chat_scam_detector': {
    name: 'Detector de estafas en el chat',
    hint: 'Oculta mensajes con pagos fuera de nuvvu, IBAN, enlaces falsos… y avisa al admin.',
    group: 'Seguridad y moderación',
  },
  'ai.price_suggestion': {
    name: 'Precio sugerido',
    hint: 'Al vender (web), botón «Sugerir precio»: rango según artículos parecidos VENDIDOS (si hay pocos, los que están a la venta) con el motivo; el vendedor decide si lo aplica. Sin datos suficientes no sugiere nada. También mantiene los vectores de anuncios. Apagado: el formulario de siempre, sin botón.',
    group: 'Inteligencia artificial',
  },
  'ai.photo_background': {
    name: 'Quitar fondo de las fotos',
    hint: 'Estudio de fotos: fondo blanco o de color.',
    group: 'Inteligencia artificial',
  },
  'ai.semantic_search': {
    name: 'Búsqueda inteligente',
    hint: 'El buscador de la web suma, en la primera página, anuncios que significan lo mismo aunque no lleven las palabras («chaqueta vaquera» ↔ «cazadora denim»), mezclados con los de siempre y con los mismos filtros. Necesita los vectores (menú «Búsqueda IA» › Calcular vectores). Apagado: búsqueda por palabras exactamente como antes.',
    group: 'Inteligencia artificial',
  },
  'ai.similar_items': {
    name: 'Artículos parecidos',
    hint: 'Bloque «También te puede interesar» al final de la ficha (web): hasta 12 anuncios activos parecidos, misma categoría primero, sin vendidos ni el mismo artículo repetido por el mismo vendedor. Apagado: la ficha sin el bloque.',
    group: 'Inteligencia artificial',
  },
  'ai.pre_moderation': {
    name: 'Revisión automática de anuncios',
    hint: 'Tras el filtro de palabras, la IA revisa cada anuncio nuevo y cada edición que cambia texto, categoría o fotos (prohibidos, falsificaciones, estafas, +18 mal marcado). Si duda, va a Moderación › Pendientes con el motivo «ai_review:…»; si está claro, se publica. Si la IA falla o tarda, se publica igual. Apagado: moderación de siempre. En «Solo admins» solo revisa anuncios de cuentas admin.',
    group: 'Seguridad y moderación',
  },
  'ai.dispute_assistant': {
    name: 'Asistente de quejas',
    hint: 'Resume cada queja y propone una solución al admin.',
    group: 'Inteligencia artificial',
  },
  'ai.support_bot': {
    name: 'Bot de soporte',
    hint: 'Responde primero a los tickets; si se apaga, todo va directo a una persona.',
    group: 'Inteligencia artificial',
  },
  'ai.seller_suggested_replies': {
    name: 'Respuestas sugeridas al vendedor',
    hint: 'En el chat de la web, al vendedor le salen 2–3 respuestas cortas al último mensaje del comprador, en su idioma; al tocar una se escribe en el campo (nunca se envía sola). Apagado: el chat de siempre.',
    group: 'Inteligencia artificial',
  },
  'ai.social_copy': {
    name: 'Textos para redes sociales',
    hint: 'Escribe los posts automáticos de Facebook/Instagram.',
    group: 'Inteligencia artificial',
  },
  'ai.duplicate_accounts': {
    name: 'Detectar cuentas duplicadas',
    hint: 'Aviso a los admins (push/correo y lista en «Cuentas duplicadas») cuando una cuenta comparte dispositivo, tarjeta, cuenta bancaria de cobro o teléfono verificado con otra, bloqueada o no. Nunca bloquea a nadie. Apagado: no se apunta nada nuevo; la lista se queda como está.',
    group: 'Seguridad y moderación',
  },
  social_publisher: {
    name: 'Publicar en redes automáticamente',
    hint: 'Publica anuncios en Facebook/Instagram a sus horas (en el sandbox, siempre apagado).',
    group: 'Funciones de la plataforma',
  },
  handover_mode: {
    name: 'Entrega en mano',
    hint: 'Permite comprar y quedar en persona con código de entrega.',
    group: 'Funciones de la plataforma',
  },
  nuvvu_club: {
    name: 'Club nuvvu',
    hint: 'Suscripción de compradores (2,99 €/mes o 29,99 €/año, primer mes gratis): 1 % en puntos, 1 € de bienvenida, sin parte fija de protección en 6 compras al mes, 72 h más para reclamar, soporte humano, ofertas 2 días antes y avisos 2 h antes. Apagado: sin altas ni puntos nuevos; lo pagado se respeta.',
    group: 'Funciones de la plataforma',
  },
  pro_subscriptions: {
    name: 'Suscripciones PRO',
    hint: 'Se pueden contratar planes PRO.',
    group: 'Funciones de la plataforma',
  },
  featured_packs: {
    name: 'Destacados de pago',
    hint: 'Se pueden comprar destacados.',
    group: 'Funciones de la plataforma',
  },
  bumps: {
    name: 'Subir anuncios (bumps)',
    hint: 'Se puede pagar por subir un anuncio arriba.',
    group: 'Funciones de la plataforma',
  },
  forum: {
    name: 'Foro',
    hint: 'La comunidad y sus hilos.',
    group: 'Funciones de la plataforma',
  },
  country_signup_guard: {
    name: 'Alta solo desde países permitidos',
    hint: 'Bloquea registros desde países fuera de la lista.',
    group: 'Seguridad y moderación',
  },
  listing_human_review: {
    name: 'Revisión humana de anuncios nuevos',
    hint: 'Los anuncios nuevos esperan a que un admin los apruebe en Moderación.',
    group: 'Seguridad y moderación',
  },
  chat_spam_guard: {
    name: 'Freno al spam del chat',
    hint: 'Cuentas nuevas (< 7 días, sin compras ni ventas): si mandan lo mismo a varios, un fraude claro o la IA lo detecta → bloqueo, mensajes ocultos y aviso. Sin fotos en el chat los 3 primeros días; máx. 3 chats nuevos el primer día.',
    group: 'Seguridad y moderación',
  },
  promo_no_commission: {
    name: 'Promoción sin comisión',
    hint: 'Aplica las campañas de descuento en la protección (Campañas de descuento): el comprador paga ese % menos, hasta 100 % = sin comisión. Apagado: protección normal; los pedidos ya hechos no cambian.',
    group: 'Funciones de la plataforma',
  },
  promo_cashback: {
    name: 'Devolución al monedero (cashback)',
    hint: 'Aplica las campañas de cashback: al completarse el pedido vuelve un % como saldo de premio (no se retira). Apagado: no se prometen nuevos; lo ya prometido se da igual.',
    group: 'Funciones de la plataforma',
  },
  pro_auto_banners: {
    name: 'Banners automáticos PRO Absoluto',
    hint: 'Cada día crea, actualiza y quita el banner de portada (arriba) de cada PRO Absoluto con su último producto activo; los banners manuales no se tocan. Apagado (o «Solo admins», que para el job cuenta como apagado): no crea, cambia ni borra nada; los ya creados se quedan hasta que los borres en Publicidad.',
    group: 'Funciones de la plataforma',
  },
  video_ads: {
    name: 'Anuncios de vídeo',
    hint: 'Los anuncios propios con vídeo se reproducen en la web (en bucle y sin sonido). Apagado: solo se ve su portada; no se borra nada. «Solo admins»: solo los administradores ven el vídeo.',
    group: 'Funciones de la plataforma',
  },
  rewards: {
    name: 'Premios y ventajas',
    hint: 'Envío gratis y protección gratis por usuario y fechas (Premios). Apagado: el checkout cobra lo de siempre.',
    group: 'Funciones de la plataforma',
  },
};

export const flagInfo = (key: string): FlagInfo =>
  FLAG_INFO[key] ?? { name: key, hint: '', group: 'Funciones de la plataforma' };
